import axios from 'axios';

import { API_BASE_URL } from './apiConfig';

// Two-stage authentication interfaces
export interface LoginRequest {
  username: string;
  password: string;
}

export interface VendorOption {
  vendorId: number;
  vendorName: string;
}

export interface UserInfo {
  id: number;
  username: string;
  email?: string;
}

// First stage response
export interface LoginResponse {
  preAuthToken: string;
  user: UserInfo;
  availableVendors: VendorOption[];
}

// Second stage request
export interface SelectContextRequest {
  vendorId: number;
}

// Second stage response
export interface AuthContextResponse {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
  currentVendorId: number;
  currentVendorName: string;
  permissions: string[];
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
}

// Legacy interface for backward compatibility
export interface AuthUser {
  id?: string | number;
  username?: string;
  email?: string;
  [key: string]: unknown;
}

export interface LoginResponseData {
  accessToken: string;
  refreshToken: string;
  expiresAt?: string | number;
  user: AuthUser;
}

// Stage 1: Identity authentication
export async function loginStage1({ username, password }: LoginRequest): Promise<LoginResponse> {
  const response = await axios.post<ApiResponse<LoginResponse>>(`${API_BASE_URL}/auth/login`, {
    username,
    password,
  });

  const apiResponse = response?.data;

  if (!apiResponse?.success) {
    throw new Error(apiResponse?.message || 'Login failed');
  }

  const loginData = apiResponse?.data;

  if (!loginData?.preAuthToken || !loginData?.user || !loginData?.availableVendors?.length) {
    throw new Error('Invalid login response');
  }

  // Store pre-auth token temporarily
  sessionStorage.setItem('preAuthToken', loginData.preAuthToken);
  sessionStorage.setItem('loginUser', JSON.stringify(loginData.user));

  return loginData;
}

// Stage 2: Vendor selection and get full access
export async function loginStage2({ vendorId }: SelectContextRequest): Promise<AuthContextResponse> {
  const preAuthToken = sessionStorage.getItem('preAuthToken');
  
  if (!preAuthToken) {
    throw new Error('Pre-auth token not found. Please login again.');
  }

  const response = await axios.post<ApiResponse<AuthContextResponse>>(
    `${API_BASE_URL}/auth/context`,
    { vendorId },
    {
      headers: {
        Authorization: `Bearer ${preAuthToken}`,
      },
    }
  );

  const apiResponse = response?.data;

  if (!apiResponse?.success) {
    throw new Error(apiResponse?.message || 'Vendor selection failed');
  }

  const contextData = apiResponse?.data;

  if (!contextData?.accessToken || !contextData?.refreshToken) {
    throw new Error('Invalid vendor selection response');
  }

  // Clear temporary data and store final tokens
  sessionStorage.removeItem('preAuthToken');
  sessionStorage.removeItem('loginUser');
  
  localStorage.setItem('accessToken', contextData.accessToken);
  localStorage.setItem('refreshToken', contextData.refreshToken);
  localStorage.setItem('currentVendorId', contextData.currentVendorId.toString());
  localStorage.setItem('currentVendorName', contextData.currentVendorName);
  localStorage.setItem('permissions', JSON.stringify(contextData.permissions));
  
  // Store user info for compatibility
  const loginUser = sessionStorage.getItem('loginUser');
  if (loginUser) {
    const user = JSON.parse(loginUser);
    localStorage.setItem('user', JSON.stringify({
      ...user,
      vendorId: contextData.currentVendorId,
      vendorName: contextData.currentVendorName,
    }));
  }

  return contextData;
}

// Legacy login function for backward compatibility
export async function login({ username, password }: LoginRequest): Promise<LoginResponseData> {
  // For now, throw error to force migration to two-stage login
  throw new Error('Please use the new two-stage login process');
}

export function logout(): void {
  // Clear all tokens and user data
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  localStorage.removeItem('currentVendorId');
  localStorage.removeItem('currentVendorName');
  localStorage.removeItem('permissions');
  
  // Clear temporary session data
  sessionStorage.removeItem('preAuthToken');
  sessionStorage.removeItem('loginUser');
}

// Get current user permissions
export function getCurrentPermissions(): string[] {
  const permissions = localStorage.getItem('permissions');
  return permissions ? JSON.parse(permissions) : [];
}

// Check if user has specific permission
export function hasPermission(permission: string): boolean {
  const permissions = getCurrentPermissions();
  return permissions.includes(permission);
}

// Get current vendor info
export function getCurrentVendor(): { id: number; name: string } | null {
  const vendorId = localStorage.getItem('currentVendorId');
  const vendorName = localStorage.getItem('currentVendorName');
  
  if (!vendorId || !vendorName) {
    return null;
  }
  
  return {
    id: parseInt(vendorId, 10),
    name: vendorName,
  };
}

// Check if user is authenticated with full access
export function isAuthenticated(): boolean {
  const accessToken = localStorage.getItem('accessToken');
  const vendorId = localStorage.getItem('currentVendorId');
  return !!(accessToken && vendorId);
}
